import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { LeadCardData, Stage, UserOption } from "@/types/leadflow"

type Row = {
  id: number
  name: string
  company_name: string | null
  phone: string | null
  email: string | null
  external_status: string | null
  field_routes_id: string | null
  do_not_text: boolean
  leads: LeadCardData[]
}

type Pagination = {
  page: number
  per_page: number
  total_count: number
  total_pages: number
  prev_page: number | null
  next_page: number | null
}

type Props = {
  customers: Row[]
  stages: Stage[]
  users: UserOption[]
  statuses: string[]
  filters: Record<string, string>
  pagination: Pagination
}

export default function CustomersIndex({ customers, stages, users, statuses, filters, pagination }: Props) {
  const currentFilters = {
    q: filters.q ?? "",
    stage_id: filters.stage_id ?? "",
    assignee_id: filters.assignee_id ?? "",
    status: filters.status ?? "",
  }

  const queryForPage = (page: number) => ({
    ...currentFilters,
    page,
    per_page: pagination.per_page,
  })

  const submitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    router.get(
      "/customers",
      {
        q: String(values.q ?? ""),
        stage_id: String(values.stage_id ?? ""),
        assignee_id: String(values.assignee_id ?? ""),
        status: String(values.status ?? ""),
        page: 1,
        per_page: pagination.per_page,
      },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  const goToPage = (page: number) => {
    router.get("/customers", queryForPage(page), {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const firstRecord = pagination.total_count === 0 ? 0 : (pagination.page - 1) * pagination.per_page + 1
  const lastRecord = Math.min(pagination.page * pagination.per_page, pagination.total_count)

  return (
    <>
      <Head title="Customers">
        <meta name="description" content="Browse FieldRoutes customer context and LeadFlow activity." />
        <meta property="og:title" content="Customers" />
        <meta property="og:description" content="Browse FieldRoutes customer context and LeadFlow activity." />
      </Head>
      <AppShell>
        <PageHeader title="Customers" description={`${pagination.total_count} customers total. Showing ${firstRecord}–${lastRecord || 0}.`} />
        <form className="mt-6 grid gap-3 rounded-lg border border-hairline bg-surface p-4 md:grid-cols-4" onSubmit={submitFilters}>
          <label className="md:col-span-2">
            Search
            <Input name="q" defaultValue={currentFilters.q} placeholder="Name, phone, email, address, or FieldRoutes ID" />
          </label>
          <label>
            Stage
            <Select name="stage_id" defaultValue={currentFilters.stage_id}>
              <option value="">All stages</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </label>
          <label>
            Assignee
            <Select name="assignee_id" defaultValue={currentFilters.assignee_id}>
              <option value="">All assignees</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
            </Select>
          </label>
          <label>
            Status
            <Select name="status" defaultValue={currentFilters.status}>
              <option value="">Any status</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </label>
          <div className="flex items-end gap-2">
            <Button><Search className="h-4 w-4" />Apply</Button>
            <Button type="button" variant="ghost" onClick={() => router.get("/customers", { per_page: pagination.per_page }, { preserveState: true, replace: true })}>Clear</Button>
          </div>
        </form>

        <div className="mt-6 overflow-hidden rounded-lg border border-hairline bg-page">
          <div className="border-b border-hairline px-4 py-3 text-sm text-ink-muted">
            Showing {firstRecord || 0} to {lastRecord || 0} of {pagination.total_count}
          </div>
          <ul className="divide-y divide-hairline">
            {customers.map((customer) => (
              <li key={customer.id}>
                <Link href={`/customers/${customer.id}`} className="block p-4 no-underline hover:bg-surface">
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <strong>{customer.name}</strong>
                      {customer.company_name && <p className="text-sm">{customer.company_name}</p>}
                      <p className="mt-1 text-sm text-ink-muted">{customer.phone || "No phone"} · {customer.email || "No email"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {customer.external_status && <Badge>{customer.external_status}</Badge>}
                      {customer.do_not_text && <Badge tone="signal">Do not text</Badge>}
                      <Badge tone="accent">{customer.leads.length} {customer.leads.length === 1 ? "lead" : "leads"}</Badge>
                    </div>
                  </div>
                  {customer.leads[0] && (
                    <p className="mt-2 text-sm">
                      Current stage: <strong>{customer.leads[0].stage.name}</strong> · {customer.leads[0].assignee?.email || "Unassigned"}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
          {customers.length === 0 && <p className="p-8 text-center">No customers match these filters.</p>}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-muted">
            Page {pagination.page} of {pagination.total_pages}. {pagination.total_count} customers total.
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" disabled={!pagination.prev_page} onClick={() => pagination.prev_page && goToPage(pagination.prev_page)}>
              Previous
            </Button>
            <Button type="button" variant="ghost" disabled={!pagination.next_page} onClick={() => pagination.next_page && goToPage(pagination.next_page)}>
              Next
            </Button>
          </div>
        </div>
      </AppShell>
    </>
  )
}
